import { Trans, useTranslation } from 'react-i18next';
import { BeddingIcon, CheckIcon, CrossIcon } from '../Icons/Icons';
import styles from '../../pages/Home/Home.module.css';

/* The two halves of the hygiene comparison, left to right: the home machine
   and its mold, then the commercial drum that has nowhere for it to grow. */
const COMPARE_SIDES = [
  { key: 'problem', file: 'smelly-mold.jpeg', bad: true },
  { key: 'answer', file: 'no-mold.jpeg', bad: false },
] as const;

/**
 * The health-and-hygiene argument: the material-quality statement, the home
 * machine beside the STACKCOMBO, and the bedding reminder it pays off in.
 *
 * Shared by the tenant page and the management page, so the copy always comes
 * from the `home` namespace (`why.*`) and the styles from the Home module —
 * one wording and one look wherever the argument is made.
 */
const HygieneCompare = ({
  statement = true,
  payoff = true,
}: {
  /** The "Material quality" statement above the cards. */
  statement?: boolean;
  /** The baby photo and bedding reminder below them. */
  payoff?: boolean;
}) => {
  const { t } = useTranslation('home');

  const list = <T,>(key: string) =>
    t(key, { returnObjects: true }) as unknown as T[];

  const rowLabels = list<string>('why.compare.rowLabels');

  return (
    <>
      {/* The material-quality statement is the section's argument in one
          sentence, so it is set as a statement rather than as a lead
          paragraph. The comparison cards below summarise it; they must not
          repeat it word for word. */}
      {statement && (
        <div className={styles.materialStatement} data-reveal>
          <div className={styles.materialLabel}>{t('why.leadLabel')}</div>
          <p>
            <Trans i18nKey="why.lead" ns="home" components={{ hl: <span /> }} />
          </p>
        </div>
      )}

      {/* The argument is a contrast, so it is staged as one: the home machine
          beside the STACKCOMBO, answering the same three questions in the same
          order. The mold photo is the evidence for the left column, and its tag
          has to name whose machine it is — an unlabelled photo of mold on our
          own page reads as mold in ours. */}
      <div className={styles.cmpGrid}>
        {COMPARE_SIDES.map(({ key, file, bad }, side) => (
          <div
            key={key}
            className={`${styles.cmpCard} ${bad ? styles.cmpBad : styles.cmpGood}`}
            data-reveal
            data-reveal-delay={side * 110}
          >
            <figure className={styles.cmpPhoto}>
              <img src={`/images/${file}`} alt={t(`why.compare.${key}.imageAlt`)} />
              <figcaption className={styles.cmpTag}>
                {t(`why.compare.${key}.tag`)}
              </figcaption>
            </figure>
            <div className={styles.cmpBody}>
              <h3>{t(`why.compare.${key}.title`)}</h3>
              <dl className={styles.cmpRows}>
                {rowLabels.map((label, i) => (
                  <div key={label}>
                    <dt>{bad ? <CrossIcon /> : <CheckIcon />}{label}</dt>
                    <dd>{list<string>(`why.compare.${key}.points`)[i]}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        ))}
      </div>

      {/* The payoff: the mechanism above is the argument, this is what it is
          for — a wide, short photo band with the reminder under it. A resident
          reminder, so the management page leaves it off. */}
      {payoff && (
        <div className={styles.payoff} data-reveal>
          <img src="/images/lh/healthier-baby.png" alt={t('why.payoff.imageAlt')} />
          <div className={styles.whyChip}><BeddingIcon />{t('why.payoff.chip')}</div>
        </div>
      )}
    </>
  );
};

export default HygieneCompare;
